import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Anchor, Box, FormField, FormHelp, Label, Paragraph, Textarea, TextButton } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/web-base';

import { useRegisterFieldEditModalHeaderAction } from '../../../contexts/FieldEditModalHeaderActionContext';
import {
  useIsOnReviewStep,
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
  useMultiplexUpdate,
} from '../../../hooks/queries/multiplex';
import { useGetListingDetails, useGetListingRecommendations } from '../../../hooks/queries/properties';
import {
  PLEDGE_VIOLATION_MESSAGE,
  useCheckDiscriminatoryContent,
} from '../../../hooks/queries/useCheckDiscriminatoryContent';
import { useCanAccessAIListingCreationTestB } from '../../../hooks/useAIListingCreation';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { useMultiEditMutation } from '../../../hooks/useMultiEditMutation';
import { getFirstUnitError } from '../../../utils/multiplexUpdateErrorUtils';
import propertyUtils from '../../../utils/propertyUtils';

import { RECS } from '../../../constants/RecommendationsConstants';
import { EXTERNAL_URLS } from '../../../constants/urls';

import RecommendationModal from '../../modals/RecommendationModal';

import PropertyDescriptionStrength from '../../PropertyDescriptionStrength';
import RevertButton from '../../RevertButton';

import type { ListingAlias } from '@zg-rentals/ts-rental-manager-api-client';

const apiFieldName = 'fullDescription';

const PropertyDescriptionFormField = ({
  isRelistingFieldset = false,
  showLabel = true,
  aiDescription,
}: {
  isRelistingFieldset?: boolean;
  showLabel?: boolean;
  aiDescription?: string;
}) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const isOnReviewStep = useIsOnReviewStep();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });
  const { data: multiplexData } = useMultiplexDetails({
    enabled: !isOnReviewStep,
  });
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  const recommendationTypes = new Set(recommendationsData?.recommendations?.map((r) => r.type) ?? []);
  const initialDescription =
    !isOnReviewStep && multiplexData?.fullDescription
      ? multiplexData.fullDescription
      : (listingDetails?.fullDescription ?? '');

  const label = isRoomForRent ? 'Room and shared space description' : 'Property description';
  const inputName = isRoomForRent ? 'roomDescription' : 'description';
  const isRequired = isRoomForRent;

  const [multiplexError, setMultiplexError] = useState<string | undefined>(undefined);
  const [pledgeError, setPledgeError] = useState<string | undefined>(undefined);
  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  const updateMutation = useMultiEditMutation();
  const checkDiscriminatoryContent = useCheckDiscriminatoryContent();
  const isAiListingFlow = useCanAccessAIListingCreationTestB();

  const descriptionField = useFieldSave({
    fieldName: inputName,
    apiFieldName: 'fullDescription',
    initialValue: initialDescription,
    mutation: updateMutation,
    externalError: pledgeError,
    buildMutationVariables: (value) => ({
      postData: {
        property: {
          listingAlias,
          fullDescription: value,
        },
      },
    }),
  });

  const hasAppliedAi = useRef(false);
  useEffect(() => {
    if (!aiDescription || hasAppliedAi.current) {
      return;
    }

    // Only fill an empty field. The step waits behind the drafting screen, so anything already here
    // came from an earlier save rather than from typing over the draft.
    if (!descriptionField.value?.trim()) {
      hasAppliedAi.current = true;
      descriptionField.onChange(aiDescription);
      // Saved here as well as shown. The field only persists on blur, so a landlord who never
      // focuses it would reach the review page with the draft missing. Multiplex is excluded
      // because the AI flow never runs there.
      if (isOnReviewStep || !isInMultiplexListingFlow) {
        void enqueueSave(async () => {
          setPledgeError(undefined);
          await saveDescription(aiDescription);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDescription, descriptionField.value]);

  // Keyed on the draft itself, not on whether this render applied it. The applied flag is component
  // state, so a reload cleared it and Revert vanished exactly when the text had been edited.
  const canRevertToAiDescription = Boolean(aiDescription && descriptionField.value !== aiDescription);

  const { mutateAsync: multiplexUpdateAsync } = useMultiplexUpdate({
    meta: {
      context: {
        api: 'useMultiplexUpdate',
        details: 'PropertyDescriptionFormField',
        fieldName: apiFieldName,
      },
    },
  });

  // Takes the value explicitly: Revert saves in the same tick as its onChange, before the
  // descriptionField.value state has caught up.
  const saveDescription = async (value: string) => {
    setMultiplexError(undefined);

    if (isOnReviewStep || !isInMultiplexListingFlow) {
      descriptionField.onBlur?.();
      return;
    }

    if (!multiplexBuildingSessionId) {
      logError({
        error: new Error('Unable to save: missing multiplexBuildingSessionId'),
        context: { details: 'PropertyDescriptionFormField.saveDescription', fieldName: apiFieldName },
      });
      return;
    }

    try {
      const response = await multiplexUpdateAsync({
        postData: { multiplexBuildingSessionId, fullDescription: value },
      });

      const unitErrorMessage = getFirstUnitError(response.units, {
        fieldName: apiFieldName,
        fallbackMessage: 'Unable to save description',
        responseFailed: response.success === false,
      });

      if (unitErrorMessage) {
        setMultiplexError(unitErrorMessage);
      }
    } catch (err) {
      const message = DatarouterError.is(err)
        ? DatarouterError.getDisplayMessage(err)
        : ((err as { message?: string })?.message ?? '');
      setMultiplexError(message);
    }
  };

  // Clicking Revert blurs the textarea first, so a save of the edited text is already under way when
  // the click lands. Chain every save through one queue so the restored draft is always the last
  // write, instead of two requests racing and the discarded text sometimes winning.
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const enqueueSave = (run: () => Promise<void>) => {
    const next = saveQueueRef.current.then(run, run);
    saveQueueRef.current = next.catch(() => undefined);
    return next;
  };

  const runBlurSave = async () => {
    setPledgeError(undefined);

    // Runs before the save, so a violation blocks it rather than being reported after it.
    if (isAiListingFlow && descriptionField.value?.trim() && listingAlias) {
      try {
        const { containsDiscriminatoryContent } = await checkDiscriminatoryContent({
          listingAlias: listingAlias as ListingAlias,
          text: descriptionField.value,
        });

        if (containsDiscriminatoryContent) {
          setPledgeError(PLEDGE_VIOLATION_MESSAGE);
          return;
        }
      } catch (err) {
        // A check that cannot run must not block the landlord from saving.
        logError({
          error: err instanceof Error ? err : new Error('Discriminatory content check failed'),
          context: { details: 'PropertyDescriptionFormField.handleBlur', fieldName: apiFieldName },
        });
      }
    }

    await saveDescription(descriptionField.value);
  };

  const handleBlur = () => enqueueSave(runBlurSave);

  // The pledge check is skipped: the draft is AI-written, and a block here would leave the field
  // showing text the server does not have. Clearing the error runs inside the queued step so a
  // pledge failure from the blur save cannot land on the restored draft.
  const handleRevert = () => {
    if (!aiDescription) {
      return;
    }

    descriptionField.onChange(aiDescription);
    void enqueueSave(async () => {
      setPledgeError(undefined);
      await saveDescription(aiDescription);
    });
  };

  // Hosted next to the section heading, or in the field edit modal header, whichever wraps this field.
  const hasHeaderActionHost = useRegisterFieldEditModalHeaderAction(
    canRevertToAiDescription
      ? { ariaLabel: 'Revert to the AI-written description', onAction: handleRevert }
      : undefined,
  );

  const displayError = pledgeError ?? multiplexError ?? descriptionField.error;

  return (
    <div data-id="PropertyDescriptionFormField">
      <FormField
        error={!!displayError}
        description={
          displayError && (
            <FormHelp>
              <span dangerouslySetInnerHTML={{ __html: displayError }} />
            </FormHelp>
          )
        }
        required={isRequired}
        label={
          isRelistingFieldset ? (
            <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
              <Label>{label}</Label>
              {recommendationTypes.has(isRoomForRent ? RECS.ROOM_DESCRIPTION : RECS.DESCRIPTION) && (
                <TextButton
                  textStyle="body-sm-bold"
                  onClick={() => {
                    setDisplayRecsModal(true);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          ) : showLabel ? (
            <Label>{label}</Label>
          ) : null
        }
      >
        <Textarea
          name={inputName}
          value={descriptionField.value}
          rows={10}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => descriptionField.onChange(e.target.value)}
          onBlur={handleBlur}
          data-testid="PropertyDescriptionFormField-Textarea"
        />
        {Boolean(!displayError && descriptionField.value) && (
          <PropertyDescriptionStrength description={descriptionField.value} />
        )}
        {/* Travels with the field so the edit modal carries it too, not just the wizard step. */}
        {Boolean(aiDescription) && (
          <Paragraph textStyle="body-sm-bold" css={{ marginTop: '400' }}>
            This draft was checked for alignment with our{' '}
            <Anchor href={EXTERNAL_URLS.PLEDGE} target="_blank" rel="noopener noreferrer">
              Respectful Renting Pledge
            </Anchor>
            .
          </Paragraph>
        )}
        {canRevertToAiDescription && !hasHeaderActionHost && (
          <Box css={{ marginTop: '400' }}>
            <RevertButton ariaLabel="Revert to the AI-written description" onRevert={handleRevert} />
          </Box>
        )}
      </FormField>
      {isRelistingFieldset && displayRecsModal && (
        <RecommendationModal onClose={() => setDisplayRecsModal(false)} recommendationType={RECS.DESCRIPTION} />
      )}
    </div>
  );
};

export default PropertyDescriptionFormField;
